import { useEffect, useState } from "react";

/**
 * Single source of document scroll state for the whole app.
 *
 * NextOpp scrolls the window, never an inner pane (see the Stage A spec), so
 * one passive listener here serves every scroll-aware component. Consumers must
 * not attach their own — a page can render 200 job cards and three competing
 * listeners is three times the work on every frame.
 */
function readScroll() {
  const range = document.documentElement.scrollHeight - window.innerHeight;
  const y = window.scrollY;
  return {
    // Guard the divide: on a page that fits the viewport, range is 0.
    progress: range > 0 ? Math.min(1, Math.max(0, y / range)) : 0,
    isScrolled: y > window.innerHeight,
    isScrollable: range > 0,
  };
}

export default function useScrollProgress() {
  const [state, setState] = useState(readScroll);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      setState((prev) => {
        const next = readScroll();
        // Bail out when nothing changed. Without this, every scroll frame
        // re-renders each consumer — and on JobSearch that re-renders a
        // 200-card list.
        return prev.progress === next.progress
          && prev.isScrolled === next.isScrolled
          && prev.isScrollable === next.isScrollable
          ? prev
          : next;
      });
    };

    const onEvent = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onEvent, { passive: true });
    window.addEventListener("resize", onEvent, { passive: true });

    // Results load asynchronously and change document height after mount, so
    // read once on mount rather than trusting the initial useState value.
    update();

    return () => {
      window.removeEventListener("scroll", onEvent);
      window.removeEventListener("resize", onEvent);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return state;
}
